/**
 * The overflow menu, on `details` so the browser owns the open state.
 *
 * Three had been hand-rolled with three different open mechanisms: hover, click
 * and `:focus-within`. The focus-within one opened whenever a keyboard passed
 * through it and closed the moment focus moved to the item being reached for.
 *
 * `details` gives open and close without JavaScript. Escape it does not: no
 * browser closes a `details` on Escape, so that, the outside click and the
 * focus restore after a choice are what this adds.
 *
 * @module components/ui/Dropdown
 */

import React, {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";

import { FloatingTip, type FloatingTipTriggerProps } from "./FloatingTip";
import { Icon } from "./Icon";
import { useDropdownPlacement } from "./useDropdownPlacement";

export interface MenuItem {
  key: string;
  label: string;
  icon?: string;
  onSelect: () => void;
  /** Refused but still reachable, as `aria-disabled` plus a click guard. */
  disabled?: boolean;
  /** Why it is refused, under its label and as its description. */
  disabledReason?: string;
  /** Colours the item for a destructive action. */
  tone?: "default" | "danger";
  /** A rule above it, setting a destructive or unrelated entry apart. */
  separated?: boolean;
  "data-testid"?: string;
}

/** How the control that opens the menu is painted. */
export const DROPDOWN_TRIGGERS = [
  "bare",
  "ghost",
  "ghost-circle",
  "outline",
  "outline-square",
  "primary",
] as const;
export type DropdownTrigger = (typeof DROPDOWN_TRIGGERS)[number];

/** Exported so `safelist.test.ts` can reach every class a prop can build. */
export const DROPDOWN_TRIGGER_CLASS: Record<DropdownTrigger, string> = {
  bare: "list-none",
  ghost: "list-none btn btn-ghost btn-sm btn-square",
  // Round, for a trigger whose content is already round: an avatar.
  "ghost-circle": "list-none btn btn-ghost btn-sm btn-circle",
  outline: "list-none btn btn-outline btn-sm",
  // An icon-only trigger beside outline buttons: same height and weight.
  "outline-square": "list-none btn btn-outline btn-sm btn-square",
  primary: "list-none btn btn-primary btn-sm btn-square",
};

/** A menu's width, or a wider one for a panel of content. */
export const DROPDOWN_PANEL_WIDTH = {
  menu: "w-64",
  wide: "w-[min(24rem,calc(100vw-2rem))]",
} as const;

interface DropdownBaseProps {
  /**
   * What the trigger shows: an icon, an avatar, a label. Content only, never a
   * button — the `summary` is already the control, and a button inside it
   * nests two, which doubles the tab stops and reads as a button in a button.
   */
  trigger: ReactNode;
  /**
   * Paints the `summary` itself, because the summary is the control: a button
   * inside it would be a second tab stop for one action.
   */
  triggerAppearance?: DropdownTrigger;
  /** Extra classes on the `summary`, for a trigger that needs a larger target. */
  triggerClassName?: string;
  /** Names the menu, since a kebab says nothing about what is in it. */
  "aria-label": string;
  align?: "start" | "end";
  /** Keeps the panel this many px from the top of the viewport, below a fixed bar. */
  minTop?: number;
  /** Sits above the items: whose menu this is, or what it acts on. */
  header?: ReactNode;
  className?: string;
  "data-testid"?: string;
  /** A tooltip over the trigger, which an icon alone leaves unexplained. */
  triggerTip?: string;
  /** On the `summary`, which is the control a test presses. */
  triggerTestId?: string;
}

/**
 * A menu of actions, or a panel of content: a feed, a form. A panel is no
 * menu, so it takes no menu role and no arrow keys, only the open and close.
 */
export type DropdownProps = DropdownBaseProps &
  (
    | { items: readonly MenuItem[]; panel?: never; panelWidth?: never }
    | {
        /** A control inside that finishes with it closes it by `useDropdownClose`. */
        panel: ReactNode;
        panelWidth?: keyof typeof DROPDOWN_PANEL_WIDTH;
        items?: never;
      }
  );

const DropdownCloseContext = createContext<((restoreFocus?: boolean) => void) | null>(null);

/** Closes the panel it is called inside; `true` puts focus back on the trigger. */
export function useDropdownClose(): (restoreFocus?: boolean) => void {
  const close = useContext(DropdownCloseContext);
  if (!close) {
    throw new Error("useDropdownClose must be called inside a Dropdown panel");
  }
  return close;
}

/**
 * @example
 * ```tsx
 * <Dropdown aria-label="Programme actions" trigger={<Icon name="dots-three" />} items={items} />
 * ```
 */
/** How far each arrow moves through the items. */
const ARROW_STEP: Record<string, number | undefined> = { ArrowDown: 1, ArrowUp: -1 };

export function Dropdown({
  trigger,
  triggerAppearance = "bare",
  triggerClassName = "",
  items = undefined,
  panel = undefined,
  panelWidth = undefined,
  "aria-label": label,
  align = "end",
  minTop = undefined,
  header = undefined,
  className = "",
  "data-testid": testId,
  triggerTestId = undefined,
  triggerTip = undefined,
}: DropdownProps): React.ReactElement {
  const ref = useRef<HTMLDetailsElement>(null);
  const [activeKey, setActiveKey] = useState<string | undefined>(undefined);
  const menuRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef(new Map<string, HTMLButtonElement>());
  const reasonPrefix = useId();

  const place = useDropdownPlacement({ detailsRef: ref, panelRef: menuRef, align, minTop });

  const close = useCallback((restoreFocus = false) => {
    if (!ref.current?.open) {
      return;
    }
    // Focus first: closing hides the children, and the item that was focused
    // goes with them, dropping focus to the body so the next Tab restarts at
    // the top of the page.
    if (restoreFocus) {
      ref.current.querySelector("summary")?.focus();
    }
    ref.current.open = false;
  }, []);

  useEffect(() => {
    const onDocumentPointerDown = (event: MouseEvent): void => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        close();
      }
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== "Escape" || !ref.current?.open) {
        return;
      }
      // Stopped here so the shell's own Escape handler does not act on a key
      // this menu has already consumed and close the navigation drawer behind
      // it while the menu stays open. preventDefault as well as stopPropagation,
      // because a <dialog>'s Escape is a default action rather than a listener:
      // without it, Escape in a menu inside a modal closes the modal too.
      event.stopPropagation();
      event.preventDefault();
      close(true);
    };
    // Tabbing out closes it too. Left open, it keeps consuming Escape for the
    // whole page: the capture listener above would swallow the key before the
    // shell's own handler saw it, so Escape stopped closing the drawer.
    const onFocusIn = (event: FocusEvent): void => {
      if (ref.current?.open && !ref.current.contains(event.target as Node)) {
        close();
      }
    };
    document.addEventListener("mousedown", onDocumentPointerDown);
    document.addEventListener("keydown", onKeyDown, true);
    document.addEventListener("focusin", onFocusIn);
    return () => {
      document.removeEventListener("mousedown", onDocumentPointerDown);
      document.removeEventListener("keydown", onKeyDown, true);
      document.removeEventListener("focusin", onFocusIn);
    };
  }, [close]);

  /*
   * One tab stop for the whole menu, which is the other half of what
   * `role="menu"` promises: bare buttons made a five-item menu five stops, so
   * Tab walked through the menu rather than past the control that opens it.
   * Arrow keys move within it and the stop follows, so Tab returns where the
   * menu was left. A refused item is reached like any other, so its reason is.
   */
  const menu = items ?? [];
  const stopKey = menu.some((entry) => entry.key === activeKey) ? activeKey : menu[0]?.key;

  const summary = (tip?: Partial<FloatingTipTriggerProps>): React.ReactElement => (
    <>
      {/* The summary is the button. Styling it, rather than putting a button
          inside it, keeps one control where there is one action. */}
      <summary
        {...tip}
        aria-describedby={undefined}
        className={`${DROPDOWN_TRIGGER_CLASS[triggerAppearance]} ${triggerClassName}`.trim()}
        aria-label={label}
        data-testid={triggerTestId}
        // An arrow on the trigger enters the menu, which is the other half of
        // the promise role="menu" makes: ArrowDown lands on the first item,
        // ArrowUp on the last, both without a mouse.
        onKeyDown={(event) => {
          const step = ARROW_STEP[event.key];
          if (!step || !ref.current || panel) {
            return;
          }
          event.preventDefault();
          ref.current.open = true;
          // Placed here as well as on `toggle`, which is queued: without it the
          // menu paints at daisyUI's unclamped position for a frame.
          place();
          const target = step > 0 ? menu[0] : menu[menu.length - 1];
          if (target) {
            itemRefs.current.get(target.key)?.focus();
          }
        }}
      >
        {trigger}
      </summary>
    </>
  );

  return (
    <details
      ref={ref}
      className={`dropdown ${align === "end" ? "dropdown-end" : ""} ${className}`.trim()}
      data-testid={testId}
    >
      {triggerTip ? (
        <FloatingTip tip={triggerTip} placement="top">
          {(tip) => summary(tip)}
        </FloatingTip>
      ) : (
        summary()
      )}
      <div
        ref={menuRef}
        className={`dropdown-content z-[60] mt-1 overflow-hidden rounded-box border border-base-300 bg-base-200 shadow-lg transition-none [animation:none] ${DROPDOWN_PANEL_WIDTH[panel ? (panelWidth ?? "menu") : "menu"]}`}
      >
        {header}
        {panel ? (
          <DropdownCloseContext.Provider value={close}>{panel}</DropdownCloseContext.Provider>
        ) : (
          <ul
            className="menu w-full p-1"
            // The menu role belongs on the list, not the details: the summary is
            // the button that opens it, and the items are what the role describes.
            role="menu"
            aria-label={label}
          >
            {menu.map((item, index) => {
              const reason = item.disabled ? item.disabledReason : undefined;
              const reasonId = `${reasonPrefix}-${index}`;
              return (
                // `.disabled` is what daisyUI reads to withhold the hover fill.
                <React.Fragment key={item.key}>
                  {item.separated && index > 0 ? (
                    <li role="separator" className="my-1 border-t border-base-content/20 p-0" />
                  ) : null}
                  <li role="none" className={item.disabled ? "disabled" : undefined}>
                    <button
                      type="button"
                      role="menuitem"
                      aria-disabled={item.disabled || undefined}
                      aria-describedby={reason ? reasonId : undefined}
                      tabIndex={item.key === stopKey ? 0 : -1}
                      onFocus={() => {
                        setActiveKey(item.key);
                      }}
                      onClick={() => {
                        if (item.disabled) {
                          return;
                        }
                        item.onSelect();
                        close(true);
                      }}
                      // `role="menu"` promises arrow-key navigation, so it has to be
                      // there: without it a screen-reader user hears a menu, presses
                      // ArrowDown and nothing moves.
                      onKeyDown={(event) => {
                        const step = ARROW_STEP[event.key];
                        if (!step) {
                          return;
                        }
                        event.preventDefault();
                        const at = menu.findIndex((entry) => entry.key === item.key);
                        const next = menu[(at + step + menu.length) % menu.length];
                        if (next) {
                          itemRefs.current.get(next.key)?.focus();
                        }
                      }}
                      ref={(node) => {
                        if (node) {
                          itemRefs.current.set(item.key, node);
                        } else {
                          itemRefs.current.delete(item.key);
                        }
                      }}
                      className={[
                        "flex w-full items-center gap-2 text-sm",
                        item.disabled ? "cursor-not-allowed text-muted" : "",
                        !item.disabled && item.tone === "danger" ? "text-error-strong" : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      data-testid={item["data-testid"]}
                    >
                      {item.icon && <Icon name={item.icon} aria-hidden />}
                      <span className="min-w-0 text-left">
                        {item.label}
                        {/* Hidden from the name, reached by the description. A
                      tooltip would be clipped by the menu's own overflow. */}
                        {reason && (
                          <span id={reasonId} aria-hidden className="block text-xs">
                            {reason}
                          </span>
                        )}
                      </span>
                    </button>
                  </li>
                </React.Fragment>
              );
            })}
          </ul>
        )}
      </div>
    </details>
  );
}
